import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import {
  completeWelcome,
  dismissUsageTips,
  getStatus,
  markUsageTipSeen,
  restoreUsageTips,
  skipWelcome,
  type UsageTip,
} from "../../../services/onboardingService";

const STEP_STORAGE = "adatis.firstExperience.welcomeStep";

export function clearWelcomeProgress(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(STEP_STORAGE);
}

type WelcomeStep = "intro" | "service" | "hours" | "appointment" | "done";

const WELCOME_STEPS: WelcomeStep[] = [
  "intro",
  "service",
  "hours",
  "appointment",
  "done",
];

const readWelcomeStep = (): WelcomeStep => {
  if (typeof window === "undefined") {
    return "intro";
  }

  const stored = window.sessionStorage.getItem(STEP_STORAGE);

  return WELCOME_STEPS.includes(stored as WelcomeStep)
    ? (stored as WelcomeStep)
    : "intro";
};

const writeWelcomeStep = (step: WelcomeStep): void => {
  window.sessionStorage.setItem(STEP_STORAGE, step);
};

const clearWelcomeStep = (): void => {
  clearWelcomeProgress();
};

interface FirstExperienceModalProps {
  onHoldAutoPrompts: (hold: boolean) => void;
}

/**
 * Owner first session (presentation) and one usage tip per later visit.
 * Server state is shared with the Expo app. GAP-F2-110.
 */
const FirstExperienceModal = ({ onHoldAutoPrompts }: FirstExperienceModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [welcomeStep, setWelcomeStep] = useState<WelcomeStep | null>(null);
  const [tip, setTip] = useState<UsageTip | null>(null);
  const [schedulePath, setSchedulePath] = useState("/units");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await getStatus();
      const welcome = response.data.welcome;
      const usageTip = response.data.usage_tip ?? null;

      const scheduleStep = response.data.quickstart.steps.find(
        (step) => step.key === "unit_schedule"
      );
      if (scheduleStep?.cta) {
        setSchedulePath(scheduleStep.cta);
      }

      if (welcome?.show) {
        onHoldAutoPrompts(true);
        setTip(null);
        setWelcomeStep(readWelcomeStep());
        return;
      }

      clearWelcomeStep();
      onHoldAutoPrompts(false);
      setWelcomeStep(null);
      setTip(usageTip);
    } catch {
      onHoldAutoPrompts(false);
      setWelcomeStep(null);
      setTip(null);
    }
  }, [onHoldAutoPrompts]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!tip) {
      return;
    }

    void markUsageTipSeen().catch(() => {
      // The tip still closes this visit locally if the stamp fails later.
    });
  }, [tip]);

  const closeWelcome = () => {
    clearWelcomeStep();
    setWelcomeStep(null);
    onHoldAutoPrompts(false);
  };

  const handleSkip = async () => {
    setBusy(true);
    try {
      await skipWelcome();
      closeWelcome();
    } catch {
      toast.error(t("onboarding.firstExperience.toast.actionError"));
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    setBusy(true);
    try {
      await completeWelcome();
      closeWelcome();
    } catch {
      toast.error(t("onboarding.firstExperience.toast.actionError"));
    } finally {
      setBusy(false);
    }
  };

  const goTo = (path: string, next: WelcomeStep) => {
    writeWelcomeStep(next);
    setWelcomeStep(next);
    navigate(path);
  };

  const handleWelcomePrimary = () => {
    if (welcomeStep === "intro") {
      writeWelcomeStep("service");
      setWelcomeStep("service");
      return;
    }

    if (welcomeStep === "service") {
      goTo("/services", "hours");
      return;
    }

    if (welcomeStep === "hours") {
      goTo(schedulePath, "appointment");
      return;
    }

    if (welcomeStep === "appointment") {
      goTo("/agenda?action=create", "done");
      return;
    }

    void handleComplete();
  };

  const handleTipPrimary = async () => {
    if (!tip) {
      return;
    }

    if (tip.action === "copy_link") {
      if (!tip.url) {
        toast.error(t("onboarding.quickstart.toast.copyLinkMissing"));
        return;
      }

      try {
        await navigator.clipboard.writeText(tip.url);
        toast.success(t("onboarding.quickstart.toast.copyLinkSuccess"));
      } catch {
        toast.error(t("onboarding.quickstart.toast.copyLinkError"));
      }

      setTip(null);
      return;
    }

    if (tip.action === "extension" || tip.action === "play_store") {
      if (tip.url) {
        window.open(tip.url, "_blank", "noopener,noreferrer");
      }
      setTip(null);
      return;
    }

    if (tip.path) {
      navigate(tip.path);
    }

    setTip(null);
  };

  const handleDismissTips = async () => {
    setBusy(true);
    try {
      await dismissUsageTips();
      setTip(null);
      toast.success(
        <span className="d-inline-flex flex-wrap align-items-center gap-2">
          <span>{t("onboarding.firstExperience.toast.dismissed")}</span>
          <button
            type="button"
            className="btn btn-sm btn-light py-0 px-2"
            onClick={() => {
              void (async () => {
                try {
                  const restored = await restoreUsageTips();
                  setTip(restored.data.usage_tip);
                  toast.success(t("onboarding.firstExperience.toast.restoreSuccess"));
                } catch {
                  toast.error(t("onboarding.firstExperience.toast.restoreError"));
                }
              })();
            }}
          >
            {t("onboarding.firstExperience.toast.undo")}
          </button>
        </span>,
        { autoClose: 8000 }
      );
    } catch {
      toast.error(t("onboarding.firstExperience.toast.actionError"));
    } finally {
      setBusy(false);
    }
  };

  if (welcomeStep) {
    return (
      <Modal isOpen centered backdrop="static" keyboard={false}>
        <ModalHeader>{t(`onboarding.firstExperience.welcome.${welcomeStep}.title`)}</ModalHeader>
        <ModalBody>
          <p className="mb-0">{t(`onboarding.firstExperience.welcome.${welcomeStep}.body`)}</p>
        </ModalBody>
        <ModalFooter className="d-flex flex-column align-items-stretch gap-2">
          <Button color="primary" disabled={busy} onClick={handleWelcomePrimary}>
            {t(`onboarding.firstExperience.welcome.${welcomeStep}.primary`)}
          </Button>
          {welcomeStep === "intro" ? (
            <Button color="link" disabled={busy} onClick={() => void handleSkip()}>
              {t("onboarding.firstExperience.welcome.intro.secondary")}
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    );
  }

  if (!tip) {
    return null;
  }

  return (
    <Modal isOpen centered toggle={() => setTip(null)}>
      <ModalHeader toggle={() => setTip(null)}>
        {t(`onboarding.firstExperience.tips.${tip.key}.title`)}
      </ModalHeader>
      <ModalBody>
        <p className="mb-0">{t(`onboarding.firstExperience.tips.${tip.key}.body`)}</p>
      </ModalBody>
      <ModalFooter className="d-flex flex-column align-items-stretch gap-2">
        <Button color="primary" disabled={busy} onClick={() => void handleTipPrimary()}>
          {tip.key === "extension"
            ? t("onboarding.firstExperience.tips.extension.ctaWeb")
            : t(`onboarding.firstExperience.tips.${tip.key}.cta`)}
        </Button>
        <Button color="light" disabled={busy} onClick={() => setTip(null)}>
          {t("onboarding.firstExperience.actions.notNow")}
        </Button>
        <Button color="link" disabled={busy} onClick={() => void handleDismissTips()}>
          {t("onboarding.firstExperience.actions.dismissForever")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FirstExperienceModal;
